// Shared definitions for the Marketing Dashboard Suite (Alex Sohn's dashboard).
// Mirrors the structure of FP_Marketing Metrics Dashboard-2026.xlsx.

export type Brand = "fp" | "cb" | "fpf" | "web" | "company";
export type Platform = "instagram" | "tiktok" | "facebook" | "paid" | "ga4" | "email" | "";
export type PeriodType = "week" | "month";
export type MetricSource = "auto" | "manual";

export const BRAND_LABELS: Record<Brand, string> = {
  fp: "Faithful Platform",
  cb: "Created Bracelets",
  fpf: "Faithful Platform Foundation",
  web: "Website",
  company: "Company",
};

// ── Benchmark definitions (seeded from the Settings tab) ────────────
// direction: "higher" = at/above target is good; "lower" = at/below target is
// good (CPL, cost-per-purchase, bounce-rate ceilings).
// unit: "ratio" values are fractions (0.03 → 3%); "count"/"currency" as-is.
interface BenchmarkDef {
  key: string;
  target: number;
  direction: "higher" | "lower";
  unit: "ratio" | "count" | "currency";
  label: string;
  appliesTo: string;
  rationale: string;
}

export const BENCHMARK_DEFS: readonly BenchmarkDef[] = [
  // Engagement & growth (organic social)
  {
    key: "engagement_rate_ig",
    target: 0.03,
    direction: "higher",
    unit: "ratio",
    label: "Engagement Rate — Instagram",
    appliesTo: "FP IG, CB IG, FPF IG",
    rationale: "Industry avg 1–3%; target above average",
  },
  {
    key: "engagement_rate_tt",
    target: 0.04,
    direction: "higher",
    unit: "ratio",
    label: "Engagement Rate — TikTok",
    appliesTo: "FP TikTok",
    rationale: "TikTok avg higher due to discovery algorithm",
  },
  {
    key: "reach_growth_mom",
    target: 0.05,
    direction: "higher",
    unit: "ratio",
    label: "Reach Growth % (MoM)",
    appliesTo: "All platforms",
    rationale: "5% MoM ≈ 80% annual reach growth",
  },
  {
    key: "follower_growth_mom",
    target: 0.02,
    direction: "higher",
    unit: "ratio",
    label: "Follower Growth % (MoM)",
    appliesTo: "All platforms",
    rationale: "2% MoM is healthy for established accounts",
  },
  {
    key: "save_rate",
    target: 0.01,
    direction: "higher",
    unit: "ratio",
    label: "Save Rate (Saves ÷ Reach)",
    appliesTo: "All platforms",
    rationale: "1%+ indicates strong content resonance",
  },
  {
    key: "link_ctr",
    target: 0.015,
    direction: "higher",
    unit: "ratio",
    label: "Link CTR (Link Clicks ÷ Reach)",
    appliesTo: "All platforms",
    rationale: "1.5%+ shows content driving intent",
  },
  // FP leads & close rates
  {
    key: "fp_ig_leads_monthly",
    target: 60,
    direction: "higher",
    unit: "count",
    label: "FP Instagram Leads (monthly)",
    appliesTo: "FP Instagram",
    rationale: "Mid-high bonus tier: 275/qtr ÷ 3 × 65% IG share",
  },
  {
    key: "fp_tt_leads_monthly",
    target: 32,
    direction: "higher",
    unit: "count",
    label: "FP TikTok Leads (monthly)",
    appliesTo: "FP TikTok",
    rationale: "Mid-high bonus tier: 275/qtr ÷ 3 × 35% TT share",
  },
  {
    key: "fp_ig_close_rate",
    target: 0.19,
    direction: "higher",
    unit: "ratio",
    label: "FP Instagram Close Rate",
    appliesTo: "FP Instagram",
    rationale: "2025 actual: 19.1%",
  },
  {
    key: "fp_tt_close_rate",
    target: 0.14,
    direction: "higher",
    unit: "ratio",
    label: "FP TikTok Close Rate",
    appliesTo: "FP TikTok",
    rationale: "2025 actual: 14.0%",
  },
  {
    key: "fp_ig_new_followers_monthly",
    target: 667,
    direction: "higher",
    unit: "count",
    label: "FP Instagram — Monthly New Followers",
    appliesTo: "FP Instagram",
    rationale: "Top bonus tier 2,000/qtr ÷ 3",
  },
  // Website (weekly targets)
  {
    key: "web_organic_search_weekly",
    target: 1270,
    direction: "higher",
    unit: "count",
    label: "Organic Search — Weekly Sessions",
    appliesTo: "Website",
    rationale: "28-day avg ÷ 4 + 5% growth",
  },
  {
    key: "web_direct_weekly",
    target: 950,
    direction: "higher",
    unit: "count",
    label: "Direct — Weekly Sessions",
    appliesTo: "Website",
    rationale: "28-day avg ÷ 4",
  },
  {
    key: "web_referral_weekly",
    target: 415,
    direction: "higher",
    unit: "count",
    label: "Referral — Weekly Sessions",
    appliesTo: "Website",
    rationale: "28-day avg ÷ 4",
  },
  {
    key: "web_email_weekly",
    target: 335,
    direction: "higher",
    unit: "count",
    label: "Email — Weekly Sessions",
    appliesTo: "Website",
    rationale: "28-day avg ÷ 4 (incl. transactional)",
  },
  {
    key: "web_organic_social_weekly",
    target: 20,
    direction: "higher",
    unit: "count",
    label: "Organic Social — Weekly Sessions",
    appliesTo: "Website",
    rationale: "Low baseline; target 10% MoM growth",
  },
  {
    key: "web_total_weekly",
    target: 2990,
    direction: "higher",
    unit: "count",
    label: "Total Weekly Sessions",
    appliesTo: "Website",
    rationale: "Sum of tracked channels",
  },
  {
    key: "web_eng_rate_organic_search",
    target: 0.585,
    direction: "higher",
    unit: "ratio",
    label: "Organic Search Engagement Rate",
    appliesTo: "Website",
    rationale: "Current actual: 58.65%",
  },
  {
    key: "web_eng_rate_referral",
    target: 0.557,
    direction: "higher",
    unit: "ratio",
    label: "Referral Engagement Rate",
    appliesTo: "Website",
    rationale: "Current actual: 55.74%",
  },
  {
    key: "web_eng_rate_email",
    target: 0.573,
    direction: "higher",
    unit: "ratio",
    label: "Email Engagement Rate",
    appliesTo: "Website",
    rationale: "Current actual: 57.3%",
  },
  {
    key: "quote_page_weekly_views",
    target: 555,
    direction: "higher",
    unit: "count",
    label: "Quote Page — Weekly Views",
    appliesTo: "Website",
    rationale: "2,220 ÷ 4 weeks",
  },
  {
    key: "quote_form_interaction_rate",
    target: 0.42,
    direction: "higher",
    unit: "ratio",
    label: "Quote Page — Form Interaction Rate",
    appliesTo: "Website",
    rationale: "Current actual: 42.44%",
  },
  {
    key: "quote_conversion_rate",
    target: 0.2149,
    direction: "higher",
    unit: "ratio",
    label: "Quote Page — Conversion Rate",
    appliesTo: "Website",
    rationale: "Current actual: 21.49%",
  },
  {
    key: "weekly_quote_submissions",
    target: 120,
    direction: "higher",
    unit: "count",
    label: "Weekly Quote Submissions",
    appliesTo: "Website",
    rationale: "477 ÷ 4 weeks",
  },
  {
    key: "sotu_open_rate",
    target: 0.5,
    direction: "higher",
    unit: "ratio",
    label: "SOTU Open Rate",
    appliesTo: "Email",
    rationale: "Range 46–57%; 50% healthy midpoint",
  },
  {
    key: "sotu_click_rate",
    target: 0.01,
    direction: "higher",
    unit: "ratio",
    label: "SOTU Click Rate",
    appliesTo: "Email",
    rationale: "Range 0.5–1.7%; 1.0% realistic",
  },
  {
    key: "bounce_homepage",
    target: 0.45,
    direction: "lower",
    unit: "ratio",
    label: "Homepage Bounce Rate (max)",
    appliesTo: "Website",
    rationale: "Current 38%; stay below 45%",
  },
  {
    key: "bounce_eng_rings",
    target: 0.7,
    direction: "lower",
    unit: "ratio",
    label: "Engagement Rings Page Bounce (max)",
    appliesTo: "Website",
    rationale: "Current 75.7%; target below 70%",
  },
  {
    key: "bounce_wedding_bands",
    target: 0.75,
    direction: "lower",
    unit: "ratio",
    label: "Wedding Bands Page Bounce (max)",
    appliesTo: "Website",
    rationale: "Current 81.6%; target below 75%",
  },
  // Paid advertising
  {
    key: "fp_paid_cpl",
    target: 25,
    direction: "lower",
    unit: "currency",
    label: "FP Paid — Cost Per Lead",
    appliesTo: "FP Instagram Ads",
    rationale: "Jewelry lead-gen benchmark $20–$35",
  },
  {
    key: "fp_paid_monthly_budget",
    target: 2000,
    direction: "higher",
    unit: "currency",
    label: "FP Paid — Monthly Budget",
    appliesTo: "FP Instagram Ads",
    rationale: "$6,000 ÷ 3-month flight",
  },
  {
    key: "fp_paid_monthly_leads",
    target: 80,
    direction: "higher",
    unit: "count",
    label: "FP Paid — Monthly Leads from Ads",
    appliesTo: "FP Instagram Ads",
    rationale: "$2,000 ÷ $25 CPL",
  },
  {
    key: "cb_paid_monthly_budget",
    target: 3333,
    direction: "higher",
    unit: "currency",
    label: "CB Paid — Monthly Budget",
    appliesTo: "CB Meta Ads",
    rationale: "$10,000 ÷ 3-month flight",
  },
  {
    key: "cb_paid_roas",
    target: 3.0,
    direction: "higher",
    unit: "count",
    label: "CB Paid — ROAS",
    appliesTo: "CB Meta Ads",
    rationale: "E-commerce jewelry 3x minimum",
  },
  {
    key: "cb_paid_cpp",
    target: 35,
    direction: "lower",
    unit: "currency",
    label: "CB Paid — Cost Per Purchase",
    appliesTo: "CB Meta Ads",
    rationale: "E-commerce jewelry $25–$50",
  },
  {
    key: "cb_paid_monthly_revenue",
    target: 10000,
    direction: "higher",
    unit: "currency",
    label: "CB Paid — Monthly Revenue from Ads",
    appliesTo: "CB Meta Ads",
    rationale: "$3,333 × 3.0 ROAS",
  },
] as const;

export const BENCHMARK_BY_KEY: Record<string, BenchmarkDef> = Object.fromEntries(
  BENCHMARK_DEFS.map((b) => [b.key, b]),
);

// ── Goal display ────────────────────────────────────────────────────
/**
 * Formats a benchmark target for display beside a metric column, e.g.
 * "≥ 3.0%", "≤ $25", "≥ 13,000", "≥ 3.0x". The arrow reflects whether
 * higher or lower is the goal. Returns null when there's no target to show.
 */
export function formatGoal(
  target: number | null | undefined,
  unit: "ratio" | "count" | "currency" | "multiple",
  direction: "higher" | "lower" = "higher",
): string | null {
  if (target === null || target === undefined || !Number.isFinite(target) || target === 0) {
    return null;
  }
  const arrow = direction === "lower" ? "≤" : "≥";
  let v: string;
  if (unit === "ratio") v = `${(target * 100).toFixed(1)}%`;
  else if (unit === "currency") v = `$${Number.isInteger(target) ? target : target.toFixed(2)}`;
  else if (unit === "multiple") v = `${target}x`;
  else v = Math.round(target).toLocaleString("en-US");
  return `${arrow} ${v}`;
}
